import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type FilterItemDto, type IncidentItemDto, IncidentStatus, IncidentsV2Service, type SortItemDto } from '@api/incidents';
import { NavigationService, queryParamUtils, storeFeatures } from '@core';
import { IncidentTabStatusFilter } from '@models';
import { Actions, ofType } from '@ngrx/effects';
import { signalStore, withHooks } from '@ngrx/signals';
import { ClientManagementActions } from '@ngrx-store';
import { NotificationsService } from '@ui';

import { type IncidentsFilterCriteria } from '../../../state/incidents-criteria.store';

const PAGE_SIZE = 50;

export const DEFAULT_INCIDENTS_SORTS: SortItemDto[] = [
  { field: 'incidents.createdAt', order: 'desc' },
  { field: 'incidents.severity', order: 'asc' },
];

// Root-entity fields (`severity`/`status`/`resolvedAt`) go in bare, with no `incidents.`
// prefix — the backend's filter mechanism resolves `field` as a `FindOptionsWhere` path
// relative to the query's root entity, and `incidents` isn't a relation on `Incidents`
// (prefixing it 400s: "Property 'incidents' was not found in 'Incidents'"). This differs
// from `SORT_FIELD_MAP` below, which *does* prefix these same fields with `incidents.` —
// sorting builds a raw SQL fragment instead, where the alias is required to disambiguate
// (`severity` alone is ambiguous against the joined `incidentRule.severity`).
//
// `query` (ticket-number substring search) maps to `ticketNumber`/`contains` here — also
// bare, no prefix. The backend can't run this through its generic FindOptionsWhere path
// at all (`ticketNumber` is a numeric column; ILike needs a CAST it can't express that
// way), so it pulls this exact `{field:'ticketNumber', operator:'contains'|'startsWith'|
// 'endsWith'}` shape out of the filters array before the generic mechanism ever sees it
// and applies it as its own raw-SQL clause instead — see AV-Services-Backend's
// `dec-generic-query-filters-no-allowlist`.
//
// `spaceId` maps to a bare `spaceId`/`equals` filter — not `device.spaceId` like the
// resolved value ends up as. A floor id has to expand to its child room ids via a
// backend DB lookup this app can't replicate, so the backend pulls this exact
// `{field:'spaceId', operator:'equals'|'isAnyOf'}` shape out of the filters array,
// resolves it, and re-adds the result as its own `device.spaceId`/`isAnyOf` filter —
// same carve-out pattern as `ticketNumber` above, just resolved via a DB lookup
// instead of a raw-SQL cast.
export function buildIncidentsFilters(params: Record<string, unknown>) {
  const filters: FilterItemDto[] = [];

  if (queryParamUtils.isPresent(params['severity'])) {
    filters.push({ field: 'severity', operator: 'isAnyOf', value: params['severity'] as string });
  }

  if (queryParamUtils.isPresent(params['deviceTypeId'])) {
    filters.push({ field: 'device.deviceModel.deviceType2.id', operator: 'isAnyOf', value: params['deviceTypeId'] as string });
  }

  if (queryParamUtils.isPresent(params['deviceId'])) {
    filters.push({ field: 'device.id', operator: 'equals', value: params['deviceId'] as string });
  }

  if (queryParamUtils.isPresent(params['locationId'])) {
    filters.push({ field: 'device.locationId', operator: 'isAnyOf', value: queryParamUtils.toArray(params['locationId'] as number) });
  }

  if (queryParamUtils.isPresent(params['deviceStatus'])) {
    filters.push({ field: 'device.status', operator: 'isAnyOf', value: params['deviceStatus'] as string });
  }

  if (queryParamUtils.isPresent(params['status'])) {
    filters.push({ field: 'status', operator: 'isAnyOf', value: params['status'] as string });
  }

  if (params['startDateTime']) {
    filters.push({ field: 'resolvedAt', operator: 'onOrAfter', value: params['startDateTime'] as string });
  }

  if (params['endDateTime']) {
    filters.push({ field: 'resolvedAt', operator: 'onOrBefore', value: params['endDateTime'] as string });
  }

  if (queryParamUtils.isPresent(params['query'])) {
    filters.push({ field: 'ticketNumber', operator: 'contains', value: params['query'] as string });
  }

  if (queryParamUtils.isPresent(params['spaceId'])) {
    filters.push({ field: 'spaceId', operator: 'equals', value: params['spaceId'] as string });
  }

  return filters;
}

// Backend's `ORDER BY` interpolates `field` as a raw `alias.column` SQL reference
// (see `apply-generic-order-by.ts` / `buildOrderByExpression` in AV-Services-Backend)
// — it does NOT walk TypeORM relation property paths the way filters do. Every
// entry here has to be the query builder's actual join alias
// (`incidents.service.ts`'s `baseQueryBuilder`: device/deviceModel/deviceType/
// deviceMaker/location/incidentRule), not the nested `device.x.y.z` path Postgres
// can't parse as a column reference.
const SORT_FIELD_MAP: Record<string, string> = {
  building: 'location.friendlyName',
  deviceId: 'device.friendlyName',
  deviceType: 'deviceType.name',
  incidentRule: 'incidentRule.name',
  manufacturer: 'deviceMaker.name',
  model: 'deviceModel.name',
  reported: 'incidents.createdAt',
  resolved: 'incidents.resolvedAt',
  // The "Severity" column's `matColumnDef` is `IncidentColumn.STATUS` (='status'
  // — see `libs/models/incidents.ts`), so that's the key `mat-sort-header` actually
  // emits, not 'severity'. Both `incidents` and `device` have a `status` column, so
  // an unmapped bare `status` is an ambiguous-column SQL error, not just wrong data.
  status: 'incidents.severity',
  ticketNumber: 'incidents.ticketNumber',
};

export function mapUiSortToBackendSort(sort: SortItemDto) {
  return {
    field: SORT_FIELD_MAP[sort.field] ?? sort.field,
    order: sort.order,
  };
}

export interface IncidentsV2TableQuery {
  criteria: IncidentsFilterCriteria;
  sort: SortItemDto | null;
  tab: IncidentTabStatusFilter;
}

export const IncidentsV2TableStore = signalStore(
  storeFeatures.withPaginatedLoad<IncidentItemDto, IncidentsV2TableQuery>(() => {
    const incidentsV2Service = inject(IncidentsV2Service);
    const navigationService = inject(NavigationService);
    const notificationService = inject(NotificationsService);

    return {
      fetchPage: (query, offset) => {
        const isHistory = query.tab === IncidentTabStatusFilter.HISTORY;
        const filters = buildIncidentsFilters({
          ...query.criteria,
          ...(isHistory
            ? { status: [IncidentStatus.RESOLVED] }
            : {
              deviceStatus: ['active', 'paused'],
              status: [IncidentStatus.INPROGRESS, IncidentStatus.NEW],
            }),
        });
        const sorts = query.sort ? [mapUiSortToBackendSort(query.sort)] : DEFAULT_INCIDENTS_SORTS;

        return incidentsV2Service.findAll({ clientId: navigationService.getCurrentClientId() }, {
          filters: filters.length > 0 ? queryParamUtils.toJsonQueryParam(filters) : undefined,
          limit: PAGE_SIZE,
          offset,
          sorts: queryParamUtils.toJsonQueryParam(sorts),
        });
      },
      onError: error => notificationService.showErrorMessage(error.message),
    };
  }),
  withHooks({
    onInit(store, actions = inject(Actions), destroyRef = inject(DestroyRef)) {
      // A client switch doesn't change the query, so refetch explicitly.
      actions
        .pipe(ofType(ClientManagementActions.clientIdChanged), takeUntilDestroyed(destroyRef))
        .subscribe(() => store.reload());
    },
  }),
);
